import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  Line,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,
  ResponsiveContainer,
} from "recharts";
import { CustomTooltip } from "./components/tooltip";
import { option_colors } from "../../service/option";
import { decimalAddZero, cleanFloat } from "../../service/common";

import { classNames } from "primereact/utils";

function formatAxisTickValue({ value, isTenThousandUnit, decimalCount = 0 }) {
  if (typeof value !== "number") return value;

  return decimalAddZero({
    rawVal: cleanFloat((value ?? 0) / (isTenThousandUnit ? 10000 : 1)),
    decimalCount,
    decimalShowRaw: decimalCount === null,
  });
}

function renderBarCells({ data, dataKey, COLORS }) {
  return data.data.map((item, index) => (
    <Cell
      key={`cell_${item[dataKey] ?? item[item.key] ?? index}`}
      fill={COLORS[index]}
    />
  ));
}

function formatUnit({ unit, isTenThousandUnit }) {
  if (!unit) return "";
  return `${isTenThousandUnit ? "萬" : ""}${unit}`;
}

function getAxisTypeProps({ layout, dataKey, categoryLayout }) {
  if (layout === categoryLayout) return { dataKey, type: "category" };
  return { type: "number" };
}

function getStackProps(isStack) {
  if (!isStack) return {};
  return { stackId: "a" };
}

function getReferenceLabelPosition(axis) {
  if (axis === "x") return "top";
  return "insideBottomRight";
}

function AxisTick({
  x,
  y,
  payload,
  xyLabelColor,
  textAnchor,
  yOffset = 0,
  isTenThousandUnit,
  decimalCount = 0,
}) {
  return (
    <text
      x={x}
      y={y + yOffset}
      fill={xyLabelColor}
      textAnchor={textAnchor}
      dominantBaseline="middle"
    >
      {formatAxisTickValue({
        value: payload.value,
        isTenThousandUnit,
        decimalCount,
      })}
    </text>
  );
}

function XAxisTickRenderer(props) {
  return (
    <AxisTick
      {...props}
      textAnchor="middle"
      yOffset={10}
      decimalCount={null}
    />
  );
}

function YAxisTickRenderer(props) {
  return <AxisTick {...props} textAnchor="end" />;
}

export default function ModuleChartBar({
  className = "",
  styles = {},
  data = { isTenThousandUnit: false, keys: [], data: [] },
  COLORS = option_colors,
  options = {},
}) {
  const {
    layout = "horizontal",
    isStack = false,
    stackOptions = {
      label: null,
      sumKeys: [],
    },
    dataKey = "label",
    isDiffColor = false,
    xAxisOptions = {
      label: "",
      unit: "",
    },
    yAxisOptions = {
      isLeft: true,
      isRight: false,
      label: {},
      unit: {},
    },
    xyLabelColor = "#a3a3a3",
    isControlDomain = false,
    margin = {
      top: 40,
      right: 20,
      left: 20,
      bottom: 10,
    },
    barSize = 15,
    lineSize = 2,
    isLineDot = false,
    isTooltip = true,
    tooltipOptions = {},
    isLegend = true,
  } = options;

  const chartData = isDiffColor
    ? data.data.map((item, index) => ({
        ...item,
        color: COLORS[index],
      }))
    : data.data;
  const xAxisUnit = formatUnit({
    unit: xAxisOptions.unit,
    isTenThousandUnit: data.isTenThousandUnit,
  });
  const yLeftUnit = formatUnit({
    unit: yAxisOptions.unit?.left,
    isTenThousandUnit: data.isTenThousandUnit,
  });
  const yRightUnit = formatUnit({
    unit: yAxisOptions.unit?.right,
    isTenThousandUnit: data.isTenThousandUnit,
  });

  return (
    <div className={classNames("inline-block", className)} style={styles}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          layout={layout}
          data={chartData}
          margin={margin}
        >
          <XAxis
            stroke={xyLabelColor}
            isTenThousandUnit
            unit={xAxisUnit}
            label={xAxisOptions.label ?? ""}
            tick={
              <XAxisTickRenderer
                xyLabelColor={xyLabelColor}
                isTenThousandUnit={data.isTenThousandUnit}
              />
            }
            {...getAxisTypeProps({
              layout,
              dataKey,
              categoryLayout: "horizontal",
            })}
          />
          {yAxisOptions.isLeft !== false && (
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke={xyLabelColor}
              unit={yLeftUnit}
              tick={
                <YAxisTickRenderer
                  xyLabelColor={xyLabelColor}
                  isTenThousandUnit={data.isTenThousandUnit}
                />
              }
              {...getAxisTypeProps({
                layout,
                dataKey,
                categoryLayout: "vertical",
              })}
              {...(isControlDomain ? { domain: ["auto", "auto"] } : {})}
              label={yAxisOptions.label?.left ?? ""}
            />
          )}
          {yAxisOptions.isRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={xyLabelColor}
              unit={yRightUnit}
              {...getAxisTypeProps({
                layout,
                dataKey,
                categoryLayout: "vertical",
              })}
              {...(isControlDomain ? { domain: ["auto", "auto"] } : {})}
              label={yAxisOptions.label?.right ?? ""}
            />
          )}

          {isTooltip && (
            <Tooltip
              cursor={{ fill: "#ffffff00" }}
              content={
                <CustomTooltip
                  isDiffColor={isDiffColor}
                  isStack={isStack}
                  stackOptions={stackOptions}
                  tooltipOptions={tooltipOptions}
                  isTenThousandUnit={data.isTenThousandUnit}
                />
              }
            />
          )}
          {isLegend && <Legend />}

          {data.keys.map((item, kIdx) => (
            <React.Fragment key={item.key}>
              {item.uiType === "line" ? (
                <Line
                  yAxisId={item.yAxisId ?? "left"}
                  dataKey={item.key}
                  name={item.name ?? item.key}
                  stroke={COLORS[kIdx]}
                  strokeWidth={lineSize}
                  activeDot={{ r: 4 }}
                  dot={isLineDot}
                />
              ) : (
                <Bar
                  yAxisId={item.yAxisId ?? "left"}
                  dataKey={item.key}
                  name={item.name ?? item.key}
                  fill={COLORS[kIdx]}
                  barSize={barSize}
                  {...getStackProps(isStack)}
                >
                  {isDiffColor && renderBarCells({ data, dataKey, COLORS })}
                </Bar>
              )}
            </React.Fragment>
          ))}
          {data.referenceKey?.map((item, index) => (
            <ReferenceLine
              key={`refer_${item.axis ?? "y"}-${item.value}-${item.label ?? index}`}
              yAxisId={item.yAxisId ?? "left"}
              {...{ [item.axis === "x" ? "x" : "y"]: item.value }}
              stroke={item.color ?? "red"}
              strokeDasharray="5 5"
              ifOverflow="extendDomain"
            >
              {item.label && (
                <Label
                  value={item.label}
                  fill={item.color ?? xyLabelColor}
                  position={getReferenceLabelPosition(item.axis)}
                  offset={10}
                />
              )}
            </ReferenceLine>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

AxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  xyLabelColor: PropTypes.string,
  textAnchor: PropTypes.string,
  yOffset: PropTypes.number,
  isTenThousandUnit: PropTypes.bool,
  decimalCount: PropTypes.number,
};

XAxisTickRenderer.propTypes = {
  ...AxisTick.propTypes,
};

YAxisTickRenderer.propTypes = {
  ...AxisTick.propTypes,
};

ModuleChartBar.propTypes = {
  className: PropTypes.string,
  styles: PropTypes.object,
  data: PropTypes.shape({
    isTenThousandUnit: PropTypes.bool,
    keys: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        uiType: PropTypes.string,
        yAxisId: PropTypes.string,
      }),
    ),
    data: PropTypes.arrayOf(PropTypes.object),
    referenceKey: PropTypes.arrayOf(
      PropTypes.shape({
        axis: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        color: PropTypes.string,
        yAxisId: PropTypes.string,
      }),
    ),
  }),
  COLORS: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.object,
};
